import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

/*
 * Lightweight i18n implementation for the Safe app.
 *
 * Supported languages:
 *   en = English
 *   fr = French
 *   ar = Arabic
 *   pt = Portuguese
 *
 * Usage:
 *   const { t } = useLanguage();
 *   t('homepageHeroTitle');
 *
 * Interpolation:
 *   t('resultsFor', { count: 10, query: 'security' });
 */


// ============================================================
// TRANSLATIONS
// ============================================================

const translations = {
  // ==========================================================
  // ENGLISH
  // ==========================================================

  en: {
    // --------------------------------------------------------
    // Navigation / General
    // --------------------------------------------------------

    resources: 'Resources',
    search: 'Search',
    myResources: 'My Resources',
    supportDirectory: 'Support Directory',
    submitResource: 'Submit New Resource',
    adminDashboard: 'Admin Dashboard',
    signIn: 'Sign In',
    signOut: 'Sign Out',
    emergency: 'Emergency',
    accountSettings: 'Account Settings',

    viewAllCollections: 'View All Collections',
    popular: 'Popular:',
    allCollections: 'All Collections',

    digitalSecurity: 'Digital Security',
    legalAdvocacy: 'Legal & Advocacy',
    mentalHealth: 'Mental Health',
    emergencyProtocols: 'Emergency Protocols',

    // --------------------------------------------------------
    // Homepage
    // --------------------------------------------------------

    homepageHeroTitle: 'Welcome to the Resource Hub',

    homepageHeroSubtitle:
      'Practical tools, verified contacts, and guidance to help protect activists and rights defenders.',

    searchPlaceholder: 'Search resources...',

    inImmediateDanger: 'In Immediate Danger?',
    getHelpNow: 'Get Help Now',

    bannerSubtitle:
      '24/7 crisis support for activists and human rights defenders — trained responders ready now.',

    // Homepage sections
    quickAccessTitle: 'Quick Access to Critical Resources',
    featuredCollectionsTitle: 'Featured Collections',
    explore: 'Explore',
    resourcesCount: 'resources',
    noFeaturedCollections: 'No featured collections available yet.',

    // --------------------------------------------------------
    // Search
    // --------------------------------------------------------

    searchResultsTitle: 'Search Results',
    searching: 'Searching...',
    showFilters: 'Show Filters',
    hideFilters: 'Hide Filters',

    resourceType: 'Resource Type',
    languageLabel: 'Language',
    regionLabel: 'Region',

    applyFilters: 'Apply Filters',
    clearAll: 'Clear All',

    noResultsFound:
      'No results found for "{query}". Try different keywords or filters.',

    resultsFor: 'Showing {count} results for "{query}"',

    // --------------------------------------------------------
    // Regions
    // --------------------------------------------------------

    allRegions: 'All Regions',
    global: 'Global',
    africa: 'Africa',
    asiaPacific: 'Asia-Pacific',
    latinAmerica: 'Latin America',
    europe: 'Europe',
    mena: 'Middle East & North Africa',

    // --------------------------------------------------------
    // Services
    // --------------------------------------------------------

    allServices: 'All Services',
    digitalSecurityService: 'Digital Security',
    legalAid: 'Legal Aid',
    emergencyRelocation: 'Emergency Relocation',
    mentalHealthService: 'Mental Health',
    physicalSafety: 'Physical Safety',
    moreFilters: 'More Filters',

    // --------------------------------------------------------
    // Support Directory
    // --------------------------------------------------------

    supportDirectorySubtitle:
      'Verified organizations, hotlines, legal aid, and digital security helpdesks offering emergency and ongoing support.',

    searchOrganizationsPlaceholder:
      'Search organizations, countries, services...',

    contactSupport: 'Contact Support',
    loadMoreOrganizations: 'Load More Organizations',

    showingProviders:
      'Showing {count} of {total} verified providers',

    // --------------------------------------------------------
    // Submit Resource
    // --------------------------------------------------------

    submitResourceTitle: 'Submit a Resource',

    submissionThanksTitle: 'Resource Submitted!',

    submissionThanksMessage:
      "Thank you! Our team will review your submission shortly. You'll receive an email when it's published or if we need more info.",

    browseCollections: 'Browse Collections',
    returnToHome: 'Return to Home',
    backToHome: 'Back to Home',

    submissionSubtitle:
      'Share knowledge that helps protect activists and organizations. All submissions are reviewed by our team.',

    resourceTitleLabel: 'Resource Title *',

    titlePlaceholder:
      'e.g. Emergency Digital Security Protocol v2.1',

    resourceTypeLabel: 'Resource Type *',

    optionGuide: 'Guide / How-to',
    optionTemplate: 'Template / Checklist',
    optionPolicy: 'Policy Document',
    optionContact: 'Contact / Organization',
    optionOther: 'Other',

    descriptionLabel: 'Description *',

    descriptionPlaceholder:
      "Describe the resource, who it's for, what situations it addresses, and any key features...",

    languagesLabel: 'Languages (comma-separated)',

    languagesPlaceholder:
      'English, Spanish, French, Arabic...',

    uploadFilesLabel: 'Upload File(s)',

    dragDropText:
      'Drag & drop files here or click to browse',

    dropHintTypes:
      'PDF, DOCX, ZIP, images (max 50MB total)',

    submitting: 'Submitting...',
    submitForReview: 'Submit for Review',
    cancel: 'Cancel',

    // --------------------------------------------------------
    // Submission Guidelines
    // --------------------------------------------------------

    submissionGuidelinesTitle: 'Submission Guidelines',

    guideline1:
      'Resources should be relevant to activist safety, digital security, or human rights protection.',

    guideline2:
      'Files must be original or properly licensed (no copyrighted material without permission).',

    guideline3:
      'Provide accurate information — misleading content will be rejected.',

    guideline4:
      'We review all submissions within 5–7 business days.',

    resourceTitleRequired: 'Resource title is required.',
    descriptionRequired: 'Description is required.',

    // --------------------------------------------------------
    // Collections
    // --------------------------------------------------------

    collectionsTitle: 'Collections',

    showingCollections:
      'Showing {count} collections',

    loadingCollections: 'Loading collections...',

    errorLoadingCollections:
      'Error loading collections: {error}',

    openCollection: 'Open Collection',

    noCollectionsAvailable:
      'No collections available yet.',

    loadMoreCollections: 'Load More Collections',

    // --------------------------------------------------------
    // Login / Register
    // --------------------------------------------------------

    loginTitle: 'Sign In',

    loginSubtitle:
      'Access your saved resources, submit new ones, and manage your account.',

    registerTitle: 'Sign Up',

    registerSubtitle:
      'Create an account to save resources, submit new ones, and access your directory.',

    usernameLabel: 'Username',
    emailLabel: 'Email',
    passwordLabel: 'Password',
    confirmPasswordLabel: 'Confirm Password',

    continueWithGoogle: 'Continue with Google',
    orText: 'or',

    forgotPassword: 'Forgot password?',

    noAccountSignUp:
      "Don't have an account? Sign up",

    alreadyHaveAccount:
      'Already have an account? Sign in',

    signingIn: 'Signing in...',
    signingUp: 'Signing up...',

    submitLabel: 'Submit',
    cancelLabel: 'Cancel',

    // --------------------------------------------------------
    // Resource Detail
    // --------------------------------------------------------

    loadingResource: 'Loading resource...',
    resourceNotFound: 'Resource not found',

    homeLabel: 'Home',
    collectionsLabel: 'Collections',

    downloadLabel: 'Download',
    downloadingLabel: 'Downloading...',

    savedLabel: 'Saved',
    saveLabel: 'Save to My Resources',

    shareLabel: 'Share',

    noRatingsYet: 'No ratings yet',
    notSpecified: 'Not specified',

    fromLabel: 'From:',

    fullDescriptionHeading: 'Full Description',
    tagsHeading: 'Tags',
    relatedResourcesHeading: 'Related Resources',

    viewAllRelated: 'View all collections',

    // --------------------------------------------------------
    // Account Settings
    // --------------------------------------------------------

    accountSettingsTitle: 'Account Settings',

    loadingAccount: 'Loading account...',

    pleaseSignInAccount:
      'Please sign in to access account settings.',

    goToSignIn: 'Go to Sign In',

    personalInformation: 'Personal Information',
    organizationAndRole: 'Organization & Role',

    privacyControls: 'Privacy Controls',

    passwordAuthentication:
      'Password & Authentication',

    notificationPreferences:
      'Notification Preferences',

    emergencyAlerts:
      'Emergency Alerts & Contacts',

    dataExport:
      'Data Export & Activity',

    helpAndSupport:
      'Help & Support',

    signOutLabel: 'Sign Out',
    deleteAccount: 'Delete Account',

    // --------------------------------------------------------
    // Organization Detail
    // --------------------------------------------------------

    websiteLabel: 'Website:',
    phoneLabel: 'Phone:',
    availabilityLabel: 'Availability:',

    sendMessageNow: 'Send Message Now',

    // --------------------------------------------------------
    // Footer
    // --------------------------------------------------------

    footerResources: 'Resources',
    footerSupport: 'Support',
    footerAccount: 'Account',

    emergencySupportTitle: 'Emergency Support',

    crisisHotline: 'Crisis Hotline',

    crisisHotlineSubtitle:
      '24/7 support available',

    tagline: 'Built for activists, by activists',

    privacy: 'Privacy',
    terms: 'Terms',
    contact: 'Contact',
  },


  // ==========================================================
  // FRENCH
  // ==========================================================

  fr: {
    // Navigation / General

    resources: 'Ressources',
    search: 'Rechercher',
    myResources: 'Mes ressources',
    supportDirectory: "Annuaire d'aide",
    submitResource: 'Soumettre une ressource',
    adminDashboard: "Tableau d'administration",
    signIn: 'Se connecter',
    signOut: 'Se déconnecter',
    emergency: 'Urgence',
    accountSettings: 'Paramètres du compte',

    viewAllCollections: 'Voir toutes les collections',
    popular: 'Populaire :',
    allCollections: 'Toutes les collections',

    digitalSecurity: 'Sécurité numérique',
    legalAdvocacy: 'Droit & Plaidoyer',
    mentalHealth: 'Santé mentale',
    emergencyProtocols: "Protocoles d'urgence",

    // Homepage

    homepageHeroTitle:
      'Bienvenue dans le centre de ressources',

    homepageHeroSubtitle:
      'Outils pratiques, contacts vérifiés et conseils pour aider à protéger les militants et les défenseurs des droits.',

    searchPlaceholder:
      'Rechercher des ressources...',

    inImmediateDanger:
      'En danger immédiat ?',

    getHelpNow:
      "Obtenir de l'aide maintenant",

    bannerSubtitle:
      "Assistance de crise 24/7 pour les militants et défenseurs des droits humains — intervenants formés disponibles maintenant.",

    quickAccessTitle:
      'Accès rapide aux ressources essentielles',

    featuredCollectionsTitle:
      'Collections à la une',

    explore: 'Explorer',

    resourcesCount: 'ressources',

    noFeaturedCollections:
      'Aucune collection à la une disponible pour le moment.',

    // Search

    searchResultsTitle: 'Résultats de recherche',
    searching: 'Recherche en cours...',

    showFilters: 'Afficher les filtres',
    hideFilters: 'Masquer les filtres',

    resourceType: 'Type de ressource',
    languageLabel: 'Langue',
    regionLabel: 'Région',

    applyFilters: 'Appliquer les filtres',
    clearAll: 'Tout effacer',

    noResultsFound:
      'Aucun résultat trouvé pour "{query}". Essayez d’autres mots-clés ou filtres.',

    resultsFor:
      'Affichage de {count} résultats pour "{query}"',

    // Regions

    allRegions: 'Toutes les régions',
    global: 'Global',
    africa: 'Afrique',
    asiaPacific: 'Asie-Pacifique',
    latinAmerica: 'Amérique latine',
    europe: 'Europe',
    mena: 'Moyen-Orient & Afrique du Nord',

    // Services

    allServices: 'Tous les services',
    digitalSecurityService: 'Sécurité numérique',
    legalAid: 'Aide juridique',
    emergencyRelocation: "Relocalisation d'urgence",
    mentalHealthService: 'Santé mentale',
    physicalSafety: 'Sécurité physique',
    moreFilters: 'Plus de filtres',

    // Support Directory

    supportDirectorySubtitle:
      "Organisations vérifiées, lignes d'assistance, aide juridique et services de sécurité numérique offrant un soutien d'urgence et continu.",

    searchOrganizationsPlaceholder:
      'Rechercher organisations, pays, services...',

    contactSupport: 'Contacter le support',

    loadMoreOrganizations:
      "Charger plus d'organisations",

    showingProviders:
      'Affichage de {count} sur {total} fournisseurs vérifiés',

    // Submit Resource

    submitResourceTitle:
      'Soumettre une ressource',

    submissionThanksTitle:
      'Ressource soumise !',

    submissionThanksMessage:
      "Merci ! Notre équipe examinera votre soumission sous peu. Vous recevrez un e-mail lorsque la ressource sera publiée ou si nous avons besoin de plus d'informations.",

    browseCollections:
      'Parcourir les collections',

    returnToHome:
      "Retour à l'accueil",

    backToHome:
      "Retour à l'accueil",

    submissionSubtitle:
      'Partagez des connaissances qui aident à protéger les militants et les organisations. Toutes les soumissions sont examinées par notre équipe.',

    resourceTitleLabel:
      'Titre de la ressource *',

    titlePlaceholder:
      "ex. Protocole de sécurité numérique d'urgence v2.1",

    resourceTypeLabel:
      'Type de ressource *',

    optionGuide:
      "Guide / Mode d'emploi",

    optionTemplate:
      'Modèle / Liste de contrôle',

    optionPolicy:
      'Document de politique',

    optionContact:
      'Contact / Organisation',

    optionOther:
      'Autre',

    descriptionLabel:
      'Description *',

    descriptionPlaceholder:
      'Décrivez la ressource, pour qui elle est destinée, les situations concernées et les principales caractéristiques...',

    languagesLabel:
      'Langues (séparées par des virgules)',

    languagesPlaceholder:
      'Anglais, Espagnol, Français, Arabe...',

    uploadFilesLabel:
      'Téléverser des fichiers',

    dragDropText:
      'Glisser-déposer des fichiers ici ou cliquer pour parcourir',

    dropHintTypes:
      'PDF, DOCX, ZIP, images (max 50 Mo au total)',

    submitting:
      'Envoi en cours...',

    submitForReview:
      'Soumettre pour examen',

    cancel:
      'Annuler',

    // Submission Guidelines

    submissionGuidelinesTitle:
      'Directives de soumission',

    guideline1:
      'Les ressources doivent être pertinentes pour la sécurité des militants, la sécurité numérique ou la protection des droits humains.',

    guideline2:
      "Les fichiers doivent être originaux ou correctement licenciés (aucun matériel protégé par le droit d'auteur sans autorisation).",

    guideline3:
      'Fournissez des informations exactes — le contenu trompeur sera rejeté.',

    guideline4:
      'Nous examinons toutes les soumissions dans un délai de 5 à 7 jours ouvrables.',

    resourceTitleRequired:
      'Le titre de la ressource est requis.',

    descriptionRequired:
      'La description est requise.',

    // Collections

    collectionsTitle:
      'Collections',

    showingCollections:
      'Affichage de {count} collections',

    loadingCollections:
      'Chargement des collections...',

    errorLoadingCollections:
      'Erreur lors du chargement des collections : {error}',

    openCollection:
      'Ouvrir la collection',

    noCollectionsAvailable:
      'Aucune collection disponible pour le moment.',

    loadMoreCollections:
      'Charger plus de collections',

    // Login / Register

    loginTitle:
      'Se connecter',

    loginSubtitle:
      'Accédez à vos ressources enregistrées, soumettez-en de nouvelles et gérez votre compte.',

    registerTitle:
      "S'inscrire",

    registerSubtitle:
      'Créez un compte pour enregistrer des ressources, en soumettre de nouvelles et accéder à votre annuaire.',

    usernameLabel:
      "Nom d'utilisateur",

    emailLabel:
      'E-mail',

    passwordLabel:
      'Mot de passe',

    confirmPasswordLabel:
      'Confirmer le mot de passe',

    continueWithGoogle:
      'Continuer avec Google',

    orText:
      'ou',

    forgotPassword:
      'Mot de passe oublié ?',

    noAccountSignUp:
      "Vous n'avez pas de compte ? Inscrivez-vous",

    alreadyHaveAccount:
      'Vous avez déjà un compte ? Connectez-vous',

    signingIn:
      'Connexion...',

    signingUp:
      'Inscription...',

    submitLabel:
      'Soumettre',

    cancelLabel:
      'Annuler',

    // Resource Detail

    loadingResource:
      'Chargement de la ressource...',

    resourceNotFound:
      'Ressource introuvable',

    homeLabel:
      'Accueil',

    collectionsLabel:
      'Collections',

    downloadLabel:
      'Télécharger',

    downloadingLabel:
      'Téléchargement...',

    savedLabel:
      'Enregistré',

    saveLabel:
      'Enregistrer dans mes ressources',

    shareLabel:
      'Partager',

    noRatingsYet:
      'Pas encore de notes',

    notSpecified:
      'Non spécifié',

    fromLabel:
      'De :',

    fullDescriptionHeading:
      'Description complète',

    tagsHeading:
      'Étiquettes',

    relatedResourcesHeading:
      'Ressources associées',

    viewAllRelated:
      'Voir toutes les collections',

    // Account Settings

    accountSettingsTitle:
      'Paramètres du compte',

    loadingAccount:
      'Chargement du compte...',

    pleaseSignInAccount:
      'Veuillez vous connecter pour accéder aux paramètres du compte.',

    goToSignIn:
      'Aller à la connexion',

    personalInformation:
      'Informations personnelles',

    organizationAndRole:
      'Organisation et rôle',

    privacyControls:
      'Contrôles de confidentialité',

    passwordAuthentication:
      'Mot de passe & Authentification',

    notificationPreferences:
      'Préférences de notification',

    emergencyAlerts:
      "Alertes & contacts d'urgence",

    dataExport:
      'Exportation de données & Activité',

    helpAndSupport:
      'Aide & Support',

    signOutLabel:
      'Se déconnecter',

    deleteAccount:
      'Supprimer le compte',

    // Organization Detail

    websiteLabel:
      'Site web :',

    phoneLabel:
      'Téléphone :',

    availabilityLabel:
      'Disponibilité :',

    sendMessageNow:
      'Envoyer un message maintenant',

    // Footer

    footerResources:
      'Ressources',

    footerSupport:
      'Support',

    footerAccount:
      'Compte',

    emergencySupportTitle:
      "Assistance d'urgence",

    crisisHotline:
      "Ligne d'assistance de crise",

    crisisHotlineSubtitle:
      'Assistance disponible 24/7',

    tagline:
      'Créé pour les militants, par les militants',

    privacy:
      'Confidentialité',

    terms:
      'Conditions',

    contact:
      'Contact',
  },


  // ==========================================================
  // ARABIC
  // ==========================================================

  ar: {
    // Navigation / General

    resources: 'الموارد',
    search: 'بحث',
    myResources: 'مواردي',
    supportDirectory: 'دليل الدعم',
    submitResource: 'إرسال مورد جديد',
    adminDashboard: 'لوحة الإدارة',
    signIn: 'تسجيل الدخول',
    signOut: 'تسجيل الخروج',
    emergency: 'طوارئ',
    accountSettings: 'إعدادات الحساب',

    viewAllCollections: 'عرض كل المجموعات',
    popular: 'الشائع:',
    allCollections: 'جميع المجموعات',

    digitalSecurity: 'الأمن الرقمي',
    legalAdvocacy: 'القانون والدفاع',
    mentalHealth: 'الصحة النفسية',
    emergencyProtocols: 'بروتوكولات الطوارئ',

    // Homepage

    homepageHeroTitle:
      'مرحبًا بك في مركز الموارد',

    homepageHeroSubtitle:
      'أدوات عملية وجهات اتصال موثوقة وإرشادات للمساعدة في حماية النشطاء والمدافعين عن الحقوق.',

    searchPlaceholder:
      'البحث عن الموارد...',

    inImmediateDanger:
      'هل أنت في خطر فوري؟',

    getHelpNow:
      'الحصول على المساعدة الآن',

    bannerSubtitle:
      'دعم أزمات على مدار الساعة لنشطاء ومدافعي حقوق الإنسان — مستجيبون مدرّبون جاهزون الآن.',

    quickAccessTitle:
      'وصول سريع إلى الموارد المهمة',

    featuredCollectionsTitle:
      'المجموعات المميزة',

    explore:
      'استكشاف',

    resourcesCount:
      'موارد',

    noFeaturedCollections:
      'لا توجد مجموعات مميزة متاحة حاليًا.',

    // Search

    searchResultsTitle:
      'نتائج البحث',

    searching:
      'جارٍ البحث...',

    showFilters:
      'إظهار عوامل التصفية',

    hideFilters:
      'إخفاء عوامل التصفية',

    resourceType:
      'نوع المورد',

    languageLabel:
      'اللغة',

    regionLabel:
      'المنطقة',

    applyFilters:
      'تطبيق عوامل التصفية',

    clearAll:
      'مسح الكل',

    noResultsFound:
      'لم يتم العثور على نتائج لـ "{query}". جرّب كلمات رئيسية أو عوامل تصفية مختلفة.',

    resultsFor:
      'عرض {count} نتيجة لـ "{query}"',

    // Regions

    allRegions:
      'جميع المناطق',

    global:
      'عالمي',

    africa:
      'أفريقيا',

    asiaPacific:
      'آسيا والمحيط الهادئ',

    latinAmerica:
      'أمريكا اللاتينية',

    europe:
      'أوروبا',

    mena:
      'الشرق الأوسط وشمال أفريقيا',

    // Services

    allServices:
      'جميع الخدمات',

    digitalSecurityService:
      'الأمن الرقمي',

    legalAid:
      'المساعدة القانونية',

    emergencyRelocation:
      'إعادة توطين طارئة',

    mentalHealthService:
      'الصحة النفسية',

    physicalSafety:
      'السلامة الجسدية',

    moreFilters:
      'المزيد من المرشحات',

    // Support Directory

    supportDirectorySubtitle:
      'منظمات مُعتمدة، خطوط مساعدة، مساعدة قانونية وخدمات أمن رقمي تقدم دعمًا فوريًا ومستمرًا.',

    searchOrganizationsPlaceholder:
      'ابحث عن منظمات، دول، خدمات...',

    contactSupport:
      'اتصل بالدعم',

    loadMoreOrganizations:
      'تحميل المزيد من المنظمات',

    showingProviders:
      'عرض {count} من أصل {total} مزودًا مُعتمدًا',

    // Submit Resource

    submitResourceTitle:
      'إرسال مورد',

    submissionThanksTitle:
      'تم إرسال المورد!',

    submissionThanksMessage:
      'شكرًا! سيقوم فريقنا بمراجعة ملفك قريبًا. ستتلقى بريدًا إلكترونيًا عند نشره أو إذا كنا بحاجة إلى مزيد من المعلومات.',

    browseCollections:
      'تصفح المجموعات',

    returnToHome:
      'العودة إلى الصفحة الرئيسية',

    backToHome:
      'العودة إلى الصفحة الرئيسية',

    submissionSubtitle:
      'شارك المعرفة التي تساعد في حماية النشطاء والمنظمات. تتم مراجعة جميع الإرسالات من قِبل فريقنا.',

    resourceTitleLabel:
      'عنوان المورد *',

    titlePlaceholder:
      'مثال: بروتوكول الأمان الرقمي الطارئ v2.1',

    resourceTypeLabel:
      'نوع المورد *',

    optionGuide:
      'دليل / كيفية',

    optionTemplate:
      'قالب / قائمة تحقق',

    optionPolicy:
      'وثيقة سياسة',

    optionContact:
      'جهة اتصال / منظمة',

    optionOther:
      'أخرى',

    descriptionLabel:
      'الوصف *',

    descriptionPlaceholder:
      'وصف المورد، لمن هو موجه، وما الحالات التي يتعامل معها، وأي ميزات رئيسية...',

    languagesLabel:
      'اللغات (مفصولة بفواصل)',

    languagesPlaceholder:
      'الإنجليزية، الإسبانية، الفرنسية، العربية...',

    uploadFilesLabel:
      'رفع ملف(ملفات)',

    dragDropText:
      'اسحب وأفلت الملفات هنا أو انقر للتصفح',

    dropHintTypes:
      'PDF، DOCX، ZIP، صور (بحد أقصى 50 ميغابايت إجمالي)',

    submitting:
      'جارٍ الإرسال...',

    submitForReview:
      'إرسال للمراجعة',

    cancel:
      'إلغاء',

    // Submission Guidelines

    submissionGuidelinesTitle:
      'إرشادات الإرسال',

    guideline1:
      'يجب أن تكون الموارد ذات صلة بسلامة النشطاء، الأمن الرقمي، أو حماية حقوق الإنسان.',

    guideline2:
      'يجب أن تكون الملفات أصلية أو مرخصة بشكل صحيح (لا مواد محمية بحقوق الطبع والنشر بدون إذن).',

    guideline3:
      'قدم معلومات دقيقة — سيتم رفض المحتوى المضلل.',

    guideline4:
      'نراجع جميع الإرسالات خلال 5-7 أيام عمل.',

    resourceTitleRequired:
      'عنوان المصدر مطلوب.',

    descriptionRequired:
      'الوصف مطلوب.',

    // Collections

    collectionsTitle:
      'المجموعات',

    showingCollections:
      'عرض {count} مجموعة',

    loadingCollections:
      'جارٍ تحميل المجموعات...',

    errorLoadingCollections:
      'حدث خطأ أثناء تحميل المجموعات: {error}',

    openCollection:
      'فتح المجموعة',

    noCollectionsAvailable:
      'لا توجد مجموعات متاحة حاليًا.',

    loadMoreCollections:
      'تحميل المزيد من المجموعات',

    // Login / Register

    loginTitle:
      'تسجيل الدخول',

    loginSubtitle:
      'ادخل إلى مواردك المحفوظة، قدم موارد جديدة، وقم بإدارة حسابك.',

    registerTitle:
      'إنشاء حساب',

    registerSubtitle:
      'أنشئ حسابًا لحفظ الموارد، وإرسال موارد جديدة، والوصول إلى الدليل الخاص بك.',

    usernameLabel:
      'اسم المستخدم',

    emailLabel:
      'البريد الإلكتروني',

    passwordLabel:
      'كلمة المرور',

    confirmPasswordLabel:
      'تأكيد كلمة المرور',

    continueWithGoogle:
      'المتابعة باستخدام Google',

    orText:
      'أو',

    forgotPassword:
      'نسيت كلمة المرور؟',

    noAccountSignUp:
      'لا تملك حسابًا؟ سجّل',

    alreadyHaveAccount:
      'هل لديك حساب بالفعل؟ تسجيل الدخول',

    signingIn:
      'جارٍ تسجيل الدخول...',

    signingUp:
      'جارٍ التسجيل...',

    submitLabel:
      'إرسال',

    cancelLabel:
      'إلغاء',

    // Resource Detail

    loadingResource:
      'جارٍ تحميل المورد...',

    resourceNotFound:
      'المورد غير موجود',

    homeLabel:
      'الرئيسية',

    collectionsLabel:
      'المجموعات',

    downloadLabel:
      'تنزيل',

    downloadingLabel:
      'جارٍ التنزيل...',

    savedLabel:
      'محفوظ',

    saveLabel:
      'حفظ إلى مواردي',

    shareLabel:
      'مشاركة',

    noRatingsYet:
      'لا توجد تقييمات بعد',

    notSpecified:
      'غير محدد',

    fromLabel:
      'من:',

    fullDescriptionHeading:
      'الوصف الكامل',

    tagsHeading:
      'الوسوم',

    relatedResourcesHeading:
      'موارد ذات صلة',

    viewAllRelated:
      'عرض كل المجموعات',

    // Account Settings

    accountSettingsTitle:
      'إعدادات الحساب',

    loadingAccount:
      'جارٍ تحميل الحساب...',

    pleaseSignInAccount:
      'الرجاء تسجيل الدخول للوصول إلى إعدادات الحساب.',

    goToSignIn:
      'الانتقال إلى تسجيل الدخول',

    personalInformation:
      'المعلومات الشخصية',

    organizationAndRole:
      'المنظمة والدور',

    privacyControls:
      'ضوابط الخصوصية',

    passwordAuthentication:
      'كلمة المرور والمصادقة',

    notificationPreferences:
      'تفضيلات الإشعارات',

    emergencyAlerts:
      'تنبيهات وجهات اتصال الطوارئ',

    dataExport:
      'تصدير البيانات والنشاط',

    helpAndSupport:
      'المساعدة والدعم',

    signOutLabel:
      'تسجيل الخروج',

    deleteAccount:
      'حذف الحساب',

    // Organization Detail

    websiteLabel:
      'الموقع الإلكتروني:',

    phoneLabel:
      'الهاتف:',

    availabilityLabel:
      'التوفر:',

    sendMessageNow:
      'إرسال رسالة الآن',

    // Footer

    footerResources:
      'الموارد',

    footerSupport:
      'الدعم',

    footerAccount:
      'الحساب',

    emergencySupportTitle:
      'دعم الطوارئ',

    crisisHotline:
      'خط المساعدة في الأزمات',

    crisisHotlineSubtitle:
      'الدعم متاح على مدار الساعة',

    tagline:
      'صُمم للنشطاء، بواسطة النشطاء',

    privacy:
      'الخصوصية',

    terms:
      'الشروط',

    contact:
      'اتصل بنا',
  },


  // ==========================================================
  // PORTUGUESE
  // ==========================================================

  pt: {
    // Navigation / General

    resources: 'Recursos',
    search: 'Pesquisar',
    myResources: 'Meus recursos',
    supportDirectory: 'Diretório de Apoio',
    submitResource: 'Enviar recurso',
    adminDashboard: 'Painel de administração',
    signIn: 'Entrar',
    signOut: 'Sair',
    emergency: 'Emergência',
    accountSettings: 'Configurações da conta',

    viewAllCollections:
      'Ver todas as coleções',

    popular:
      'Popular:',

    allCollections:
      'Todas as coleções',

    digitalSecurity:
      'Segurança digital',

    legalAdvocacy:
      'Direito & Advocacia',

    mentalHealth:
      'Saúde mental',

    emergencyProtocols:
      'Protocolos de emergência',

    // Homepage

    homepageHeroTitle:
      'Bem-vindo ao Centro de Recursos',

    homepageHeroSubtitle:
      'Ferramentas práticas, contatos verificados e orientações para ajudar a proteger ativistas e defensores dos direitos.',

    searchPlaceholder:
      'Pesquisar recursos...',

    inImmediateDanger:
      'Em perigo imediato?',

    getHelpNow:
      'Obter ajuda agora',

    bannerSubtitle:
      'Suporte de crise 24/7 para ativistas e defensores dos direitos humanos — respondentes treinados prontos agora.',

    quickAccessTitle:
      'Acesso rápido a recursos importantes',

    featuredCollectionsTitle:
      'Coleções em destaque',

    explore:
      'Explorar',

    resourcesCount:
      'recursos',

    noFeaturedCollections:
      'Nenhuma coleção em destaque disponível no momento.',

    // Search

    searchResultsTitle:
      'Resultados da pesquisa',

    searching:
      'Pesquisando...',

    showFilters:
      'Mostrar filtros',

    hideFilters:
      'Ocultar filtros',

    resourceType:
      'Tipo de recurso',

    languageLabel:
      'Idioma',

    regionLabel:
      'Região',

    applyFilters:
      'Aplicar filtros',

    clearAll:
      'Limpar tudo',

    noResultsFound:
      'Nenhum resultado encontrado para "{query}". Tente palavras-chave ou filtros diferentes.',

    resultsFor:
      'Mostrando {count} resultados para "{query}"',

    // Regions

    allRegions:
      'Todas as regiões',

    global:
      'Global',

    africa:
      'África',

    asiaPacific:
      'Ásia-Pacífico',

    latinAmerica:
      'América Latina',

    europe:
      'Europa',

    mena:
      'Oriente Médio e Norte da África',

    // Services

    allServices:
      'Todos os serviços',

    digitalSecurityService:
      'Segurança digital',

    legalAid:
      'Assistência jurídica',

    emergencyRelocation:
      'Reassentamento de emergência',

    mentalHealthService:
      'Saúde mental',

    physicalSafety:
      'Segurança física',

    moreFilters:
      'Mais filtros',

    // Support Directory

    supportDirectorySubtitle:
      'Organizações verificadas, linhas diretas, assistência jurídica e centros de segurança digital que oferecem suporte emergencial e contínuo.',

    searchOrganizationsPlaceholder:
      'Pesquisar organizações, países, serviços...',

    contactSupport:
      'Contatar suporte',

    loadMoreOrganizations:
      'Carregar mais organizações',

    showingProviders:
      'Mostrando {count} de {total} provedores verificados',

    // Submit Resource

    submitResourceTitle:
      'Enviar um recurso',

    submissionThanksTitle:
      'Recurso enviado!',

    submissionThanksMessage:
      'Obrigado! Nossa equipe analisará sua submissão em breve. Você receberá um e-mail quando for publicado ou se precisarmos de mais informações.',

    browseCollections:
      'Navegar coleções',

    returnToHome:
      'Retornar ao início',

    backToHome:
      'Voltar ao início',

    submissionSubtitle:
      'Compartilhe conhecimento que ajude a proteger ativistas e organizações. Todas as submissões são revisadas por nossa equipe.',

    resourceTitleLabel:
      'Título do recurso *',

    titlePlaceholder:
      'ex.: Protocolo de Segurança Digital de Emergência v2.1',

    resourceTypeLabel:
      'Tipo de recurso *',

    optionGuide:
      'Guia / Como fazer',

    optionTemplate:
      'Modelo / Lista de verificação',

    optionPolicy:
      'Documento de política',

    optionContact:
      'Contato / Organização',

    optionOther:
      'Outro',

    descriptionLabel:
      'Descrição *',

    descriptionPlaceholder:
      'Descreva o recurso, para quem é, quais situações aborda e quaisquer recursos principais...',

    languagesLabel:
      'Idiomas (separados por vírgula)',

    languagesPlaceholder:
      'Inglês, Espanhol, Francês, Árabe...',

    uploadFilesLabel:
      'Enviar arquivo(s)',

    dragDropText:
      'Arraste e solte arquivos aqui ou clique para procurar',

    dropHintTypes:
      'PDF, DOCX, ZIP, imagens (máx. 50MB no total)',

    submitting:
      'Enviando...',

    submitForReview:
      'Enviar para revisão',

    cancel:
      'Cancelar',

    // Submission Guidelines

    submissionGuidelinesTitle:
      'Diretrizes de envio',

    guideline1:
      'Os recursos devem ser relevantes para a segurança de ativistas, segurança digital ou proteção dos direitos humanos.',

    guideline2:
      'Os arquivos devem ser originais ou devidamente licenciados (sem material protegido por direitos autorais sem permissão).',

    guideline3:
      'Forneça informações precisas — conteúdo enganoso será rejeitado.',

    guideline4:
      'Revisamos todas as submissões dentro de 5–7 dias úteis.',

    resourceTitleRequired:
      'O título do recurso é obrigatório.',

    descriptionRequired:
      'A descrição é obrigatória.',

    // Collections

    collectionsTitle:
      'Coleções',

    showingCollections:
      'Mostrando {count} coleções',

    loadingCollections:
      'Carregando coleções...',

    errorLoadingCollections:
      'Erro ao carregar coleções: {error}',

    openCollection:
      'Abrir coleção',

    noCollectionsAvailable:
      'Nenhuma coleção disponível no momento.',

    loadMoreCollections:
      'Carregar mais coleções',

    // Login / Register

    loginTitle:
      'Entrar',

    loginSubtitle:
      'Acesse seus recursos salvos, envie novos e gerencie sua conta.',

    registerTitle:
      'Criar conta',

    registerSubtitle:
      'Crie uma conta para salvar recursos, enviar novos e acessar seu diretório.',

    usernameLabel:
      'Nome de usuário',

    emailLabel:
      'E-mail',

    passwordLabel:
      'Senha',

    confirmPasswordLabel:
      'Confirmar senha',

    continueWithGoogle:
      'Continuar com o Google',

    orText:
      'ou',

    forgotPassword:
      'Esqueceu a senha?',

    noAccountSignUp:
      'Não tem uma conta? Cadastre-se',

    alreadyHaveAccount:
      'Já tem uma conta? Entrar',

    signingIn:
      'Entrando...',

    signingUp:
      'Cadastrando...',

    submitLabel:
      'Enviar',

    cancelLabel:
      'Cancelar',

    // Resource Detail

    loadingResource:
      'Carregando recurso...',

    resourceNotFound:
      'Recurso não encontrado',

    homeLabel:
      'Início',

    collectionsLabel:
      'Coleções',

    downloadLabel:
      'Baixar',

    downloadingLabel:
      'Baixando...',

    savedLabel:
      'Salvo',

    saveLabel:
      'Salvar em Meus Recursos',

    shareLabel:
      'Compartilhar',

    noRatingsYet:
      'Ainda sem avaliações',

    notSpecified:
      'Não especificado',

    fromLabel:
      'De:',

    fullDescriptionHeading:
      'Descrição completa',

    tagsHeading:
      'Etiquetas',

    relatedResourcesHeading:
      'Recursos relacionados',

    viewAllRelated:
      'Ver todas as coleções',

    // Account Settings

    accountSettingsTitle:
      'Configurações da conta',

    loadingAccount:
      'Carregando conta...',

    pleaseSignInAccount:
      'Por favor, entre para acessar as configurações da conta.',

    goToSignIn:
      'Ir para Entrar',

    personalInformation:
      'Informações pessoais',

    organizationAndRole:
      'Organização & Papel',

    privacyControls:
      'Controles de privacidade',

    passwordAuthentication:
      'Senha & Autenticação',

    notificationPreferences:
      'Preferências de notificação',

    emergencyAlerts:
      'Alertas & Contatos de emergência',

    dataExport:
      'Exportação de dados & Atividade',

    helpAndSupport:
      'Ajuda & Suporte',

    signOutLabel:
      'Sair',

    deleteAccount:
      'Excluir conta',

    // Organization Detail

    websiteLabel:
      'Site:',

    phoneLabel:
      'Telefone:',

    availabilityLabel:
      'Disponibilidade:',

    sendMessageNow:
      'Enviar mensagem agora',

    // Footer

    footerResources:
      'Recursos',

    footerSupport:
      'Suporte',

    footerAccount:
      'Conta',

    emergencySupportTitle:
      'Suporte de emergência',

    crisisHotline:
      'Linha de crise',

    crisisHotlineSubtitle:
      'Suporte disponível 24/7',

    tagline:
      'Feito para ativistas, por ativistas',

    privacy:
      'Privacidade',

    terms:
      'Termos',

    contact:
      'Contato',
  },
};


// ============================================================
// LANGUAGE CONTEXT
// ============================================================

const LanguageContext = createContext(null);


// ============================================================
// LANGUAGE PROVIDER
// ============================================================

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    try {
      const stored = localStorage.getItem('safe_lang');

      // Only allow languages that actually exist.
      if (stored && translations[stored]) {
        return stored;
      }

      return 'en';
    } catch (error) {
      return 'en';
    }
  });


  // ----------------------------------------------------------
  // Update HTML language and direction
  // ----------------------------------------------------------

  useEffect(() => {
    document.documentElement.lang = lang;

    // Arabic uses right-to-left layout.
    document.documentElement.dir =
      lang === 'ar' ? 'rtl' : 'ltr';

    try {
      localStorage.setItem('safe_lang', lang);
    } catch (error) {
      // Ignore localStorage errors.
    }
  }, [lang]);


  // ----------------------------------------------------------
  // Translation function
  // ----------------------------------------------------------

  const t = (key, vars) => {
    /*
     * Look for the translation in this order:
     *
     * 1. Current language
     * 2. English fallback
     * 3. The key itself
     */

    const currentLanguageTranslations =
      translations[lang] || {};

    const englishTranslations =
      translations.en || {};

    const lookup =
      currentLanguageTranslations[key] ??
      englishTranslations[key] ??
      key;


    // --------------------------------------------------------
    // No interpolation variables
    // --------------------------------------------------------

    if (!vars) {
      return lookup;
    }


    // --------------------------------------------------------
    // Replace placeholders
    //
    // Example:
    //
    // t('resultsFor', {
    //   count: 5,
    //   query: 'security'
    // })
    //
    // becomes:
    //
    // Showing 5 results for "security"
    // --------------------------------------------------------

    return String(lookup).replace(
      /\{(\w+)\}/g,
      (_, variableName) => {
        return vars[variableName] !== undefined
          ? vars[variableName]
          : `{${variableName}}`;
      }
    );
  };


  // ----------------------------------------------------------
  // Context value
  // ----------------------------------------------------------

  const value = useMemo(
    () => ({
      lang,
      setLang,
      t,
    }),
    [lang]
  );


  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}


// ============================================================
// useLanguage Hook
// ============================================================

export function useLanguage() {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error(
      'useLanguage must be used inside a LanguageProvider'
    );
  }

  return context;
}


// ============================================================
// Default Export
// ============================================================

export default {
  translations,
};

